import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'
import setPageTitle from '@/utils/set-page-title'
import NProgress from 'nprogress'
import { isLogin } from '@/utils/auth'
import { tcTokenLoseEfficacy, tcShowToast } from '@/utils/bridge'
import { captureException } from '@/utils/sentry'
import { track } from '@/plugins/event-tracker'
import {
  getWeComBindingTokenFromRoute,
  handleWeComBindingReturn,
} from '@/composables/useWeComBindingReturn'
import { getSearchFromPath, getSearchParam, parseSearchQuery } from '@/utils/urlQuery'
import {
  applyDongchaLoginResult,
  consumeDongchaReloadReady,
  getCurrentTokenAccountUuid,
  getDongchaAccountMarkerUuid,
  hasPersistedDongchaAccountState,
  markDongchaReloadReady,
  rememberWeComReloginOrigin,
} from '@/utils/dongchaAccount'
import { clearMcpAuthRequest, storeMcpAuthRequest } from '@/views/Login/mcpAuthorizationState'
import { bindCurrentAiAccess } from '@/api/user'
import { consumeAiLoginEntry, getAiLoginEntry } from '@/utils/aiLoginEntry'
import {
  buildDongchaChannelEnterTrackParams,
  type DongchaChannelEntrySource,
  markDongchaChannelEnterTracked,
  reconcileDongchaPaymentEntry,
  resolveDongchaPaymentNavigationBoundary,
} from '@/utils/dongchaPaymentContext'

const isApp = () => /hncyQxApp/i.test(navigator.userAgent)

NProgress.configure({ showSpinner: true, parent: '#app' })

const removeMcpAuthRequestFromInitialUrl = () => {
  if (isApp()) return
  const url = new URL(window.location.href)
  if (url.pathname !== '/login' || url.searchParams.get('channel') !== 'mcp') return
  const authRequest = url.searchParams.get('auth_request')
  if (!authRequest) return

  storeMcpAuthRequest(authRequest)
  url.searchParams.delete('auth_request')
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

removeMcpAuthRequestFromInitialUrl()

const forceRefreshUserInfoRouteNames = new Set(['mine', 'account'])
const DONGCHA_SOURCE = 'dongcha'
let dongchaLoginReady = false
type DongchaLoginOutcome =
  | { status: 'success', accountChanged: boolean; isGuest: boolean }
  | { status: 'failed' }
let dongchaLoginInFlight: Promise<DongchaLoginOutcome> | null = null
let dongchaLoginNavigationId = 0

const trackDongchaLoginResult = (params: Record<string, unknown>) => {
  try {
    track('dongcha_login_result', params)
  } catch {
    console.warn('[Event Tracker] 洞察联登结果上报失败')
  }
}

const parseDongchaEntrySource = (value: string): DongchaChannelEntrySource => {
  if (!value) return 0
  if (!/^\d+$/.test(value)) return 'unknown'
  const parsed = Number(value)
  return parsed === 1 || parsed === 2 ? parsed : parsed === 0 ? 0 : 'unknown'
}

const trackDongchaChannelEnter = (isGuest: boolean, entrySource: DongchaChannelEntrySource) => {
  const entry = markDongchaChannelEnterTracked(isGuest ? 'guest' : 'linked_formal')
  if (!entry) return
  try {
    track('dongcha_channel_enter', buildDongchaChannelEnterTrackParams(entry.userIdentity, entry.paymentContext, entrySource))
  } catch {
    console.warn('[Event Tracker] 洞察渠道进入上报失败')
  }
}

const isDocumentReload = () => {
  try {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    return navigation?.type === 'reload'
  } catch {
    return false
  }
}

const getRouteQueryValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.find((item): item is string => typeof item === 'string' && item.length > 0) || ''
  }
  return typeof value === 'string' ? value : ''
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  parseQuery: parseSearchQuery,
  routes: [
    {
      path: '/',
      redirect: '/bill', // 默认跳转到账单页
    },
    {
      path: '/chat',
      name: 'chat',
      component: () => import('../views/Chat/index.vue'),
      meta: {
        title: '轻息AI',
        requiresAuth: true,
        track_name: 'chat',
      },
    },
    {
      path: '/history-chat',
      name: 'history-chat',
      component: () => import('../views/HistoryChat/index.vue'),
      meta: {
        title: '历史对话',
        requiresAuth: true,
        track_name: 'chat_history',
      },
    },
    {
      path: '/billPlatform',
      name: 'billPlatform',
      component: () => import('../views/BillPlatform/index.vue'),
      meta: {
        title: '添加平台',
        requiresAuth: true,
        track_name: 'bill_platform',
      },
    },
    {
      path: '/addBill',
      name: 'addBill',
      component: () => import('../views/BillPlatform/addBill.vue'),
      meta: {
        title: '添加平台',
        requiresAuth: true,
        track_name: 'add_bill',
      },
    },
    {
      path: '/bill',
      name: 'bill',
      component: () => import('../views/Bill/index.vue'),
      meta: {
        title: '轻息AI',
        requiresAuth: true,
        track_name: 'bill',
      },
    },
    {
      path: '/bill-demo',
      name: 'bill-demo',
      component: () => import('../views/BillDemo/index.vue'),
      meta: {
        title: '账单 Demo',
        track_name: 'bill_demo',
      },
    },
    {
      path: '/draft/edit/:draftId',
      name: 'draftEdit',
      component: () => import('../views/DraftBill/edit.vue'),
      meta: {
        title: '待确认账单',
        requiresAuth: true,
        track_name: 'draft_edit',
      },
    },
    {
      path: '/details',
      name: 'details',
      component: () => import('../views/Details/index.vue'),
      meta: {
        title: '轻息AI',
        requiresAuth: true,
        track_name: 'bill_detail',
      },
    },
    {
      path: '/messageCenter',
      name: 'messageCenter',
      component: () => import('../views/MessageCenter/index.vue'),
      meta: {
        title: '消息中心',
        requiresAuth: true,
        track_name: 'message_center',
      },
    },
    {
      path: '/weeklyReport',
      name: 'weeklyReport',
      component: () => import('../views/Report/WeeklyReport/index.vue'),
      meta: {
        title: '账单周报',
        requiresAuth: true,
        track_name: 'weekly_report',
      },
    },
    {
      path: '/monthlyReport',
      name: 'monthlyReport',
      component: () => import('../views/Report/MonthlyReport/index.vue'),
      meta: {
        title: '账单月报',
        requiresAuth: true,
        track_name: 'monthly_report',
      },
    },
    {
      path: '/mine',
      name: 'mine',
      component: () => import('../views/Mine/index.vue'),
      meta: {
        title: '我的',
        requiresAuth: true,
        track_name: 'mine',
      },
    },
    {
      path: '/account',
      name: 'account',
      component: () => import('../views/Mine/AccountSet.vue'),
      meta: {
        title: '账号设置',
        requiresAuth: true,
        track_name: 'account',
      },
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/Mine/AboutQX.vue'),
      meta: {
        title: '关于轻息AI',
        track_name: 'about',
      },
    },
    // 评审更新（2026-07-14）：本期不展示签署记录入口，保留路由代码
    // {
    //   path: '/sign-history',
    //   name: 'sign-history',
    //   component: () => import('../views/Mine/SignHistory.vue'),
    //   meta: {
    //     title: '签署记录',
    //     requiresAuth: true,
    //     track_name: 'sign_history',
    //   },
    // },
    {
      path: '/privacy-agreement',
      name: 'privacy-agreement',
      component: () => import('../views/Mine/PrivacyAgreement.vue'),
      meta: {
        title: '隐私协议',
        track_name: 'privacy_agreement',
      },
    },
    {
      path: '/privacy-agreement-app',
      name: 'privacy-agreement-app',
      component: () => import('../views/Mine/PrivacyAgreementApp.vue'),
      meta: {
        title: '隐私政策',
        track_name: 'privacy_agreement_app',
      },
    },
    {
      path: '/service-agreement',
      name: 'service-agreement',
      component: () => import('../views/Mine/ServiceAgreement.vue'),
      meta: {
        title: '服务协议',
        track_name: 'service_agreement',
      },
    },
    {
      path: '/service-agreement-app',
      name: 'service-agreement-app',
      component: () => import('../views/Mine/ServiceAgreementApp.vue'),
      meta: {
        title: '服务协议',
        track_name: 'service_agreement_app',
      },
    },
    {
      path: '/third-party-info-sharing-list',
      name: 'third-party-info-sharing-list',
      component: () => import('../views/Mine/ThirdPartyInfoSharingList.vue'),
      meta: {
        title: '三方信息共享清单',
        track_name: 'third_party_info_sharing_list',
      },
    },
    {
      path: '/personal-info-collection-list',
      name: 'personal-info-collection-list',
      component: () => import('../views/Mine/PersonalInfoCollectionList.vue'),
      meta: {
        title: '已收集个人信息明示清单',
        track_name: 'personal_info_collection_list',
      },
    },
    {
      path: '/customer-service',
      name: 'customer-service',
      component: () => import('../views/Feedback/index.vue'),
      meta: {
        title: '意见反馈',
        requiresAuth: true,
        track_name: 'user_feedback',
      },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/Login/index.vue'),
      meta: {
        title: '登录',
        track_name: 'login',
      },
    },
    {
      path: '/mcp/authorize/result',
      name: 'mcp-authorize-result',
      component: () => import('../views/McpAuthorizeResult/index.vue'),
      meta: {
        title: '轻息账单授权',
        track_name: 'mcp_authorize_result',
      },
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  const navigationId = ++dongchaLoginNavigationId

  const mcpChannel = getRouteQueryValue(to.query.channel)
  const mcpAuthRequest = getRouteQueryValue(to.query.auth_request)
  if (to.path === '/login' && mcpChannel !== 'mcp') {
    clearMcpAuthRequest()
  }
  if (!isApp() && to.path === '/login' && mcpChannel === 'mcp' && mcpAuthRequest) {
    storeMcpAuthRequest(mcpAuthRequest)
    const sanitizedQuery = { ...to.query }
    delete sanitizedQuery.auth_request
    return next({
      path: to.path,
      query: sanitizedQuery,
      hash: to.hash,
      replace: true,
    })
  }

  // 1. 优先处理免密登录逻辑 (URL 中包含 data 参数且未登录)
  const targetSearch = getSearchFromPath(to.fullPath)
  const exchangeToken = getRouteQueryValue(to.query.data) || getSearchParam('data', targetSearch)
  const source = getRouteQueryValue(to.query.source) || getSearchParam('source', targetSearch)
  const entrySource = getSearchParam('entry_source', targetSearch)
  const returnUrl = getSearchParam('return_url', targetSearch)
  const appEnvironment = isApp()
  const isDongchaH5 = !appEnvironment && source.toLowerCase() === DONGCHA_SOURCE
  const hasTrustedLoginReload = isDongchaH5 && consumeDongchaReloadReady()
  const paymentNavigationBoundary = resolveDongchaPaymentNavigationBoundary({
    hasPreviousRoute: from.matched.length > 0,
    isDocumentReload: isDocumentReload(),
    hasTrustedLoginReload,
  })
  if (!isDongchaH5) {
    dongchaLoginReady = false
  }
  if (exchangeToken && !isDongchaH5) {
    NProgress.start()
    try {
      await userStore.exchangeToken(exchangeToken)
      // 登录成功，跳转到账单页并保留来源参数
      return next(source ? { path: '/bill', query: { source } } : '/')
    } catch (error) {
      console.error('免密登录失败:', error)
      tcShowToast('获取用户信息失败')
      setTimeout(() => {
        window.history.back()
      }, 600)
      // 登录失败，终止当前导航
      return
    }
  }

  if (!appEnvironment) {
    reconcileDongchaPaymentEntry({
      source,
      entrySource,
      returnUrl,
      presence: {
        source: Boolean(getSearchParam('source', targetSearch)),
        entrySource: Boolean(getSearchParam('entry_source', targetSearch)),
        returnUrl: Boolean(getSearchParam('return_url', targetSearch)),
      },
    }, paymentNavigationBoundary, {
      preserveExistingContextOnExternalNavigation: Boolean(hasTrustedLoginReload || dongchaLoginReady),
    })
  }

  // 2. 权限拦截和信息补全
  // 只调用一次 isLogin()，复用结果
  let isLoggedIn = await isLogin()

  const aiLoginEntry = getAiLoginEntry()
  if (!isApp() && isLoggedIn && aiLoginEntry) {
    consumeAiLoginEntry()
    try {
      await bindCurrentAiAccess(aiLoginEntry)
    } catch (error) {
      console.error('AI端当前登录态绑定失败:', error)
      isLoggedIn = await isLogin()
    }
  }

  const weComBindingToken = !isApp() ? getWeComBindingTokenFromRoute(to) : ''
  const dongchaMarkerUuid = !isApp() ? getDongchaAccountMarkerUuid() : ''
  const shouldRememberWeComOrigin = Boolean(weComBindingToken && (isDongchaH5 || dongchaMarkerUuid))
  const weComOriginUuid = shouldRememberWeComOrigin
    ? userStore.userInfo?.uuid || (await getCurrentTokenAccountUuid()) || dongchaMarkerUuid
    : ''
  const weComReturnResult = await handleWeComBindingReturn(to)
  if (weComReturnResult) {
    if (weComReturnResult === true) {
      return next()
    }
    if (
      typeof weComReturnResult === 'object' &&
      'path' in weComReturnResult &&
      weComReturnResult.path === '/login'
    ) {
      rememberWeComReloginOrigin(weComBindingToken, weComOriginUuid)
    }
    return next(weComReturnResult)
  }

  if (isDongchaH5) {
    if (hasTrustedLoginReload) {
      dongchaLoginReady = true
    }
    if (!dongchaLoginReady) {
      let loginOperation = dongchaLoginInFlight
      if (!loginOperation) {
        loginOperation = (async (): Promise<DongchaLoginOutcome> => {
          NProgress.start()
          const previousUuid = userStore.userInfo?.uuid || (await getCurrentTokenAccountUuid())
          const hadPersistedAccountState = hasPersistedDongchaAccountState()
          const entryMode = exchangeToken ? 'signed_data' : 'guest'
          try {
            const loginResult = await userStore.exchangeDongchaToken(
              exchangeToken ? { secretKey: exchangeToken } : { guestLogin: true },
            )
            const accountChanged = applyDongchaLoginResult(
              loginResult,
              previousUuid,
              hadPersistedAccountState,
            )
            trackDongchaLoginResult({
              title: '洞察轻息联登结果',
              source: DONGCHA_SOURCE,
              result: 'success',
              entry_mode: entryMode,
              account_type: loginResult.mobile === null ? 'guest' : 'registered',
              had_valid_token: Boolean(previousUuid),
              account_reused: Boolean(previousUuid && previousUuid === loginResult.uuid),
              account_changed: accountChanged,
              http_status: 200,
            })
            return { status: 'success', accountChanged, isGuest: loginResult.mobile === null }
          } catch (error) {
            const response =
              error && typeof error === 'object'
                ? (error as { response?: { status?: unknown } }).response
                : undefined
            trackDongchaLoginResult({
              title: '洞察轻息联登结果',
              source: DONGCHA_SOURCE,
              result: 'fail',
              entry_mode: entryMode,
              account_type: 'unknown',
              had_valid_token: Boolean(previousUuid),
              account_reused: false,
              account_changed: false,
              http_status: typeof response?.status === 'number' ? response.status : '',
            })
            console.error(
              '[Dongcha Login] 联登失败，HTTP 状态:',
              typeof response?.status === 'number' ? response.status : 'unknown',
            )
            return { status: 'failed' }
          }
        })()
        dongchaLoginInFlight = loginOperation
        const clearLoginOperation = () => {
          if (dongchaLoginInFlight === loginOperation) {
            dongchaLoginInFlight = null
          }
        }
        void loginOperation.then(clearLoginOperation, clearLoginOperation)
      }

      const loginOutcome = await loginOperation
      // 只允许仍然有效的最新 Dongcha 导航产生跳转等页面副作用。
      if (navigationId !== dongchaLoginNavigationId) {
        return
      }
      if (loginOutcome.status === 'failed') {
        setTimeout(() => {
          window.history.back()
        }, 600)
        return
      }
      if (loginOutcome.accountChanged) {
        markDongchaReloadReady()
        window.location.replace(
          router.resolve({
            path: '/bill',
            query: { source: DONGCHA_SOURCE },
          }).href,
        )
        return
      }
      dongchaLoginReady = true
      isLoggedIn = true
      trackDongchaChannelEnter(loginOutcome.isGuest, parseDongchaEntrySource(entrySource))
      return next({
        path: '/bill',
        query: { source: DONGCHA_SOURCE },
        replace: true,
      })
    }
    if (to.path === '/account' || to.path === '/messageCenter') {
      return next({
        path: to.path === '/account' ? '/mine' : '/bill',
        query: { source: DONGCHA_SOURCE },
        replace: true,
      })
    }
  }

  if (isDongchaH5 && userStore.userInfo) {
    trackDongchaChannelEnter(userStore.userInfo.mobile === null, parseDongchaEntrySource(entrySource))
  }

  // 2.1 检查是否需要登录
  if (to.meta.requiresAuth && !isLoggedIn) {
    console.warn('需要登录才能访问此页面')
    if (isApp()) {
      tcTokenLoseEfficacy()
      return next(false)
    }

    userStore.clearAuth()
    return next({
      path: '/login',
      query: { redirect: to.fullPath },
      replace: true,
    })
  }

  // 2.2 信息补全：已登录但无用户信息 (无论是刚登录还是刷新页面)
  // Mine/Account 会直接展示用户资料，必须按当前 token 强制刷新，避免 App 异步 hydration 恢复上一用户。
  if (isLoggedIn) {
    await userStore.waitForHydration()
  }

  const shouldForceRefreshUserInfo =
    typeof to.name === 'string' && forceRefreshUserInfoRouteNames.has(to.name)

  if (isLoggedIn && (!userStore.userInfo?.uuid || shouldForceRefreshUserInfo)) {
    try {
      await userStore.fetchUserInfo({
        clearBeforeFetch: shouldForceRefreshUserInfo,
      })
    } catch (error) {
      console.error('拉取用户信息失败:', error)
      // 注意：如果 fetchUserInfo 失败（如 token 过期），
      // 通常 axios 拦截器会处理登出，或者在这里决定是否要强制跳转登录
    }
  }

  // 3. 所有检查通过，放行
  next()
})

// 设置页面标题
router.afterEach((to) => {
  setPageTitle(to.meta.title as string)
  NProgress.done()
})

// 路由错误处理
router.onError((error) => {
  console.error('路由错误:', error)
  // 上报路由错误到 Sentry
  captureException(error, {
    router: {
      message: '路由加载失败',
      error: error.message,
    },
  })
})

export default router
